import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, Inbox, MousePointerClick, Phone, Mail, Globe, BarChart3 } from "lucide-react";

const PERIODS = [{ key: "7d", label: "7 days" }, { key: "30d", label: "30 days" }, { key: "all", label: "All time" }];

export default function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("30d");
  useEffect(() => { api.get("/me/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return null;

  const t = stats.periods[period];
  const chartDays = period === "7d" ? stats.daily.slice(-7) : stats.daily;
  return (
    <section className="mt-10" data-testid="stats-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Listing stats</h2>
        <div className="flex gap-1 p-1 rounded-md border border-zinc-200 bg-white">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`stats-period-${p.key}`}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${period === p.key ? "bg-black text-white" : "hover:bg-zinc-100"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Tile Icon={Eye} label="Listing views" value={t.views} testid="stat-views" />
        <Tile Icon={Inbox} label="Enquiries" value={t.enquiries} testid="stat-enquiries" />
        <Tile Icon={MousePointerClick} label="Contact clicks" value={t.contact_clicks} testid="stat-contact-clicks"
          sub={<span className="flex gap-3"><span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{t.contact_phone}</span><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{t.contact_email}</span><span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{t.contact_website}</span></span>} />
      </div>
      <div className="mt-3 p-4 bg-white border border-zinc-200 rounded-lg" data-testid="stats-chart">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Daily activity · last {period === "7d" ? 7 : 30} days</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartDays} margin={{ left: -20, right: 8 }}>
              <CartesianGrid vertical={false} stroke="#f4f4f5" />
              <XAxis dataKey="day" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="views" name="Views" fill="#000000" radius={[3, 3, 0, 0]} />
              <Bar dataKey="contact_clicks" name="Contact clicks" fill="#FFCC00" radius={[3, 3, 0, 0]} />
              <Bar dataKey="enquiries" name="Enquiries" fill="#a1a1aa" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <OfferPerformance offers={stats.offers} />
    </section>
  );
}

function Tile({ Icon, label, value, sub, testid }) {
  return (
    <div className="p-5 bg-white border border-zinc-200 rounded-lg" data-testid={testid}>
      <p className="text-xs text-zinc-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="font-display text-3xl font-bold mt-1" data-testid={`${testid}-value`}>{value}</p>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function OfferPerformance({ offers }) {
  if (!offers.length) return null;
  return (
    <div className="mt-3 bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="offer-performance">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 px-4 pt-4">Offer performance</p>
      <table className="w-full text-sm mt-2">
        <thead className="text-left text-xs text-zinc-500">
          <tr><th className="px-4 py-2 font-medium">Offer</th><th className="px-4 py-2 font-medium">Posted</th><th className="px-4 py-2 font-medium text-right">Views</th><th className="px-4 py-2 font-medium text-right">Contact clicks</th><th className="px-4 py-2 font-medium text-right">Enquiries</th></tr>
        </thead>
        <tbody>
          {offers.map((o) => (
            <tr key={o.id} className="border-t border-zinc-100" data-testid={`offer-stats-${o.id}`}>
              <td className="px-4 py-2 font-medium">{o.title}{o.label && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#FFCC00] text-xs">{o.label}</span>}{o.removed && <span className="ml-2 text-xs text-red-600">removed</span>}</td>
              <td className="px-4 py-2 text-zinc-500">{new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
              <td className="px-4 py-2 text-right font-mono">{o.views}</td>
              <td className="px-4 py-2 text-right font-mono">{o.contact_clicks}</td>
              <td className="px-4 py-2 text-right font-mono">{o.enquiries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
